import { faFilter } from '@fortawesome/pro-regular-svg-icons';
import { Box, Flex, Stack, useMantineTheme } from '@mantine/core';
import { runInAction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Heading, Icon, Popover } from '@feathr/components';

import type { IExportsFilters } from './ExportsFilters';
import ExportsFilters from './ExportsFilters';

import * as styles from './ExportsFiltersPopover.css';

interface IProps {
  filters: IExportsFilters;
  onApply: (filters: IExportsFilters) => void;
}

/*
 * Counts every individually-selected filter value across categories. Matches
 * the "Filters (N)" semantics described in ticket #7873 — N is total
 * selections, not category-with-any-selection count.
 */
function countFilters(filters: IExportsFilters): number {
  return (filters.status__in?.length ?? 0) + (filters.export_type__in?.length ?? 0);
}

function ExportsFiltersPopover({ filters, onApply }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [isOpen, setIsOpen] = useState(false);

  const draft = useLocalObservable<IExportsFilters>(() => ({
    status__in: filters.status__in,
    export_type__in: filters.export_type__in,
  }));

  function setDraft(newFilters: IExportsFilters): void {
    runInAction(() => {
      draft.status__in = newFilters.status__in;
      draft.export_type__in = newFilters.export_type__in;
    });
  }

  // Sync the draft from applied filters every time the popover opens so the
  // "Apply" baseline is the current truth, not a leftover from a previous
  // dismissed session.
  useEffect(() => {
    if (isOpen) {
      setDraft(filters);
    }
  }, [isOpen, filters]);

  const draftCount = countFilters(draft);
  const appliedCount = countFilters(filters);

  function handleClear(): void {
    const empty: IExportsFilters = { status__in: undefined, export_type__in: undefined };
    setDraft(empty);
    onApply(empty);
    setIsOpen(false);
  }

  function handleApply(): void {
    onApply(draft);
    setIsOpen(false);
  }

  return (
    <Popover
      align={'end'}
      className={styles.popper}
      closeOnBlur={false}
      onVisibleChange={setIsOpen}
      position={'bottom'}
      visible={isOpen}
    >
      <Button prefix={<Icon icon={faFilter} />}>
        {appliedCount > 0 ? t('Filters ({{count}})', { count: appliedCount }) : t('Filters')}
      </Button>
      <>
        <Box className={styles.header}>
          <Heading order={5} variant={'h5'}>
            {t('Filters')}
          </Heading>
        </Box>
        <Stack gap={theme.spacing['3']} p={'3'}>
          <ExportsFilters filters={draft} />
        </Stack>
        <Flex gap={theme.spacing['2']} p={'3'}>
          <Button isFullWidth={true} onClick={handleClear}>
            {t('Clear ({{count}})', { count: draftCount })}
          </Button>
          <Button
            disabled={draftCount === 0}
            isFullWidth={true}
            onClick={handleApply}
            type={'primary'}
          >
            {t('Apply')}
          </Button>
        </Flex>
      </>
    </Popover>
  );
}

export default observer(ExportsFiltersPopover);
